<template>
    <div class="c-mapbox__inner">
        <filters v-if="hasFilter" :map="map" :filters="filters" :name="name">
            <div class="c-mapbox-filters__cta col-12 col-sm-4" slot="filterCTA" v-if="$slots.cta">
                <slot name="cta"></slot>
            </div>
        </filters>
        
        <div class="c-mapbox-map">
            <overlay></overlay>
            <div class="c-mapbox-map__map">
                <slot name="groups" :map="map">
                    <group :name="groupName" :type="groupType" :map="map" :clustering="clustering"></group>
                </slot>
                <div class="c-mapbox-templating__popup" v-if="marker">
                    <div class="popup-inner" :class="'popup-inner-' + marker.feature.properties.type">
                        <a class="popup-close" href="#">
                            <i class="fa fa-times" aria-hidden="true"></i>
                        </a>
                        <div class="popup-image" v-if="marker.feature.properties.image">
                            <a :href="marker.feature.properties.url"
                               :style="{ 'background-image': 'url(' + marker.feature.properties.image + ')' }"
                               :data-alt-text="ctaText"></a>
                        </div>
                        <div class="popup-body">
                            <template v-if="marker.feature.properties.type === 'Project'">
                                <p class="title">
                                    {{ marker.feature.properties.title }}
                                </p>
                                <a :href="marker.feature.properties.url"
                                   :data-alt-text="ctaText"
                                   class="cta cta-project">{{ ctaText }}</a>
                            </template>
                            <template v-if="marker.feature.properties.type === 'Office'">
                                <p class="title">
                                    <template v-if="marker.feature.properties.displayTitle">
                                        {{ marker.feature.properties.displayTitle }}
                                    </template>
                                    <template v-else-if="marker.feature.properties.address.city && marker.feature.properties.address.state">
                                        {{ marker.feature.properties.address.city }}, {{ marker.feature.properties.address.state }}
                                    </template>
                                    <template v-else>
                                        {{ marker.feature.properties.title }}
                                    </template>
                                </p>
                                <p class="address" v-if="objectHasValues(marker.feature.properties.address)">
                                    <span class="address1" v-if="marker.feature.properties.address.address1">
                                        {{ marker.feature.properties.address.address1 }}<br>
                                    </span>
                                    <span class="address2" v-if=" marker.feature.properties.address.address2">
                                        {{ marker.feature.properties.address.address2 }}<br>
                                    </span>
                                    <span class="city" v-if="marker.feature.properties.address.city">
                                        {{ marker.feature.properties.address.city + ", " }}
                                    </span>
                                    <span class="state" v-if="marker.feature.properties.address.state">
                                        {{ marker.feature.properties.address.state + " " }}
                                    </span>
                                    <span class="postal" v-if="marker.feature.properties.address.postal">
                                        {{ marker.feature.properties.address.postal }}<br>
                                    </span>
                                    <span class="phone" v-if="marker.feature.properties.phone">
                                        T: {{ marker.feature.properties.phone }}
                                    </span>
                                </p>
                                <p class="contact" v-if="objectHasValues(marker.feature.properties.contact)">
                                    <span class="name">
                                        {{ marker.feature.properties.contact.name }}<br>
                                    </span>
                                    <span class="title">
                                        {{ marker.feature.properties.contact.title }}
                                    </span>
                                </p>
                                <a :href="marker.feature.properties.url"
                                   :data-alt-text="ctaText"
                                   class="cta" target="_blank">{{ ctaText }} <i class="fa fa-external-link"></i></a>
                            </template>
                        </div>
                    </div>
                </div>
                <attribution :map="map"></attribution>
                <zoom :map="map" :position="zoomPosition"></zoom>
            </div>
        </div>
    </div>
</template>

<script>
    /*
     This file is the parent of all of Mapbox's moving parts.

     1. Filters - Turns associated Layer Groups on and off using styled checkboxes
     2. Overlay - Custom hover element that enables the map once clicked
     3. Groups - Handles any marker group layers with optional clustering
     4. Attribution - Handles the Mapbox attribution in the lower right
     5. Zoom - Custom Zoom component with basic position options
     */
    import './_mapbox.scss';
    import Map from './_mapbox';
    import Attribution from './attribution/attribution.vue';
    import Filters from './filters/filters.vue';
    import Group from './group/group.vue';
    import Overlay from './overlay/overlay.vue';
    import Zoom from './zoom/zoom.vue';

    const style = 'https://api.mapbox.com/styles/v1/digitaladmin/cjls9lysyas7t2rk4qj0vu07a/tiles/256/{z}/{x}/{y}?access_token=pk.eyJ1IjoiZGlnaXRhbGFkbWluIiwiYSI6ImNpbXdsb2dqaDAzOHB2bG00c2o3aWdyMmgifQ.hEQS_8NE5RBINFupdHhdSA';

    export default {
        name: "mapbox",
        props: {
            api: String, // GeoJSON Endpoint to load all markers
            center: {
                type: Array,
                default: () => [37.0902, -95.7129], // Default center position
            },
            clustering: {
                type: Boolean,
                default: false, // Enable marker clustering?
            },
            filters: {
                type: Object,
                default: () => ({}), // Optional Marker Group filtering
            },
            fitBounds: {
                type: Boolean,
                default: false, // Enable to zoom to outer bounds of all loaded markers
            },
            groupName: {
                type: String,
                default: '_default', // Default Group Name
            },
            groupType: {
                type: String,
                default: '_default', // Default Group Type
            },
            name: String, // Name of the map - Mostly for GTM events
            zoomPosition: {
                type: String,
                default: 'topright', // Position of the Zoom Component
            },
            zoomLevel: {
                type: Number,
                default: 4, // Initial Zoom Level on load
            },
        },
        data: function () {
            return {
                map: new Map(L.tileLayer(style)), // See: _mapbox.js - Passed to all components
                hasFilter: !!Object.keys(this.filters).length, // Are there defined filters?
                popup: L.popup({ // Single Popup layer stored as a data object
                    minWidth: 300,
                    className: 'c-mapbox-map__popup',
                    closeButton: false,
                    autoPanPaddingTopLeft: L.point(0, Map.isMobile() ? 10 : 150),
                }),
                marker: false // Current selected marker
            };
        },
        computed: {
            /*
             Simple calculated field: If the current marker is a Project, Popup CTA
             says 'View Project'. Otherwise, it's an office, and CTA says
             'View Open Position'
             */
            ctaText() {
                return this.marker.feature.properties.type === 'Project'
                    ? 'View Project'
                    : 'View Open Positions';
            },
        },
        mounted() {
            // Close the popup if the mose leaves the popup area
            $('body').on('mouseleave', '.c-mapbox-map__popup', () => {
                this.marker = null;
                this.map.map.removeLayer(this.popup);
            });
            // Close the popup if the user clicks the close button
            $('body').on('click', '.popup-close', (e) => {
                e.preventDefault();
                this.map.map.removeLayer(this.popup);
            });
            // Capture any link click events from within an open popup
            $('body').on('click', '.c-mapbox-map__popup a:not(.popup-close)', (e) => {
                const ctaCopy = $(e.target).data('alt-text');
                GTM.fire(
                    'mbiEvent',
                    `${this.name} - Link Click`,
                    `${this.marker.feature.properties.type} Location: ${this.marker.feature.properties.title}`,
                    `${ctaCopy} - ${e.target.href}`
                );
            });

            // Call the init and loading methods within the business logic class - See: _mapbox.js
            this.map.init(this.$el.querySelector('.c-mapbox-map__map'), this.center, this.zoomLevel);
            this.map.load(this.api, {
                // Bind mouse events to every marker layer
                feature: (marker) => {
                    marker.on('mouseover click contextmenu', (e) => {
                        GTM.fire(
                            'mbiEvent',
                            `${this.name} - Popup`,
                            `${e.target.feature.properties.type} Location`,
                            e.target.feature.properties.title
                        );
                        this.marker = e.target; // Set the active marker

                        // Wait for Vue to recognize the change....
                        this.$nextTick(() => {
                            // Set the innerContent string using what's in the above template markup
                            const innerContent = this.$el.querySelector('.c-mapbox-templating__popup').innerHTML;
                            this.popup
                                .setLatLng(marker.getLatLng()) // Set the Latitude and Longitude of the popup
                                .setContent(innerContent) // Fill the popup content
                                .openOn(this.map.map); // Open the popup on the map
                        });
                    });
                },
                // If we want the map to initialize and fit all available markers, this callback will fit the known boundaries
                complete: (markers) => {
                    if (this.fitBounds) {
                        this.map.fitBounds();
                    }
                }
            });
        },
        methods: {
            // Check if the object contains any non-falsey values
            objectHasValues: function (ob) {
                return !!Object.keys(ob).map(key => ob[key]).filter(v => !!v).length;
            }
        },
        components: {
            attribution: Attribution,
            filters: Filters,
            group: Group,
            overlay: Overlay,
            zoom: Zoom,
        }
    }
</script>



// WEBPACK FOOTER //
// Assets/Scripts/Components/Mapbox/mapbox.vue